import { LunchConfiguration, ModuleLunchMode, Weekday, WEEKDAYS } from './lunch.model';
import { Lunch, Module, School } from './schools.model';

const WEEKDAY_TO_SHORT: Record<string, Weekday> = {
  Mo: 'Mo',
  Di: 'Di',
  Mi: 'Mi',
  Do: 'Do',
  Fr: 'Fr',
  Montag: 'Mo',
  Dienstag: 'Di',
  Mittwoch: 'Mi',
  Donnerstag: 'Do',
  Freitag: 'Fr',
  Monday: 'Mo',
  Tuesday: 'Di',
  Wednesday: 'Mi',
  Thursday: 'Do',
  Friday: 'Fr',
  Tu: 'Di',
  We: 'Mi',
  Th: 'Do',
};

export function parseModuleDays(days: string | undefined): Weekday[] {
  if (!days) return [];
  return days
    .split(',')
    .map((d) => d.trim())
    .map((d) => WEEKDAY_TO_SHORT[d])
    .filter((d): d is Weekday => !!d);
}

export function getAvailableLunchConfigurations(
  school: School,
  module: Module,
): LunchConfiguration[] {
  const mode: ModuleLunchMode = module.lunchMode ?? 'INHERIT';
  if (mode === 'NONE') return [];

  const all = (school.lunchConfigurations ?? []).filter((c) => c.isActive);
  if (mode !== 'RESTRICTED') return all;

  const whitelist = module.availableLunchIds ?? [];
  return all.filter((c) => whitelist.includes(c.id));
}

export function getSelectableLunchDays(module: Module): Weekday[] {
  return parseModuleDays(module.schedule?.days);
}

export function calculateLunchFee(
  config: LunchConfiguration,
  selectedDays?: Weekday[],
): number {
  if (!config.fee) return 0;
  if (config.type === 'DAILY_SELECTION') {
    return config.fee.amount * (selectedDays?.length ?? 0);
  }
  return config.fee.amount;
}

export function isLunchMandatory(module: Module): boolean {
  if (module.lunchMode === 'NONE') return false;
  return !!module.mandatoryLunchId;
}

export function shouldShowLunchStep(school: School, module: Module): boolean {
  return getAvailableLunchConfigurations(school, module).length > 0;
}

export type LunchResponsibility = 'AWO' | 'EXTERNAL' | 'NOT_APPLICABLE';

/**
 * Klassifiziert, wer für das Mittagessen zuständig ist.
 *
 * - `AWO`            – es gibt mindestens eine aktive, über die AWO abgerechnete
 *                      Konfiguration (FIXED_MONTHLY oder DAILY_SELECTION).
 * - `EXTERNAL`       – die AWO organisiert kein Essen. Deckt sowohl Schulen mit
 *                      hinterlegtem Caterer als auch solche ohne jede Angabe ab.
 * - `NOT_APPLICABLE` – das Modul schliesst Essen fachlich aus (`lunchMode: 'NONE'`).
 *
 * Bewusst aus den Bestandsdaten abgeleitet, damit kein Schulfeld nachgepflegt
 * werden muss (Issue #748).
 */
export function getLunchResponsibility(
  school: School,
  module: Module,
): LunchResponsibility {
  if ((module.lunchMode ?? 'INHERIT') === 'NONE' || module.disableLunch) {
    return 'NOT_APPLICABLE';
  }
  const available = getAvailableLunchConfigurations(school, module);
  return available.some((c) => c.type !== 'EXTERNAL') ? 'AWO' : 'EXTERNAL';
}

/**
 * Ob der Mittagessen-Abschnitt in der Anmeldung erscheint. Im Gegensatz zum
 * älteren `shouldShowLunchStep` auch dann `true`, wenn es nichts auszuwählen
 * gibt — dort steht dann der Zuständigkeitshinweis (#748).
 */
export function shouldShowLunchSection(school: School, module: Module): boolean {
  return getLunchResponsibility(school, module) !== 'NOT_APPLICABLE';
}

/**
 * Beschreibung und Link des hinterlegten externen Anbieters, falls vorhanden.
 * URL wird über `ensureUrlProtocol` normalisiert.
 */
export function getExternalLunchInfo(
  school: School,
  module: Module,
): { description: string; url?: string } | null {
  const external = getAvailableLunchConfigurations(school, module).find(
    (c) => c.type === 'EXTERNAL' && !!c.externalInfo,
  );
  if (!external?.externalInfo) return null;
  const url = ensureUrlProtocol(external.externalInfo.url);
  return {
    description: external.externalInfo.description,
    ...(url ? { url } : {}),
  };
}

/**
 * Stellt sicher, dass eine URL ein Protokoll hat. Wenn der Admin nur
 * "www.example.com" eingibt, würde der Browser das als relativen Pfad
 * interpretieren (z.B. https://aktuelle-seite/de/www.example.com).
 * Fehlt das Protokoll, wird "https://" vorangestellt.
 */
export function ensureUrlProtocol(url: string | undefined | null): string {
  const trimmed = (url ?? '').trim();
  if (!trimmed) return '';
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

export { WEEKDAYS };

/**
 * Legacy-`Lunch`-Objekt aus einer LunchConfiguration - für Anzeige und
 * Backward-Compat in Anträgen. Bei DAILY_SELECTION ist der Betrag die
 * Tagesgebühr mal gewählte Tage.
 *
 * Kopie in `functions/src/_shared/resolve-start-info.ts` (dort entsteht seit
 * #1088 der gespeicherte Wert); Parity-Spec:
 * `apps/parity-tests/legacy-lunch.parity.spec.ts`.
 */
export function deriveLegacyLunch(
  config: LunchConfiguration | undefined,
  selectedDays: Weekday[]
): Lunch | null {
  if (!config) return null;

  if (config.type === 'EXTERNAL') {
    return {
      description: config.externalInfo?.description || config.name,
      externalLunch: true,
      ...(config.externalInfo?.url ? { url: config.externalInfo.url } : {}),
    };
  }

  const totalAmount =
    config.type === 'DAILY_SELECTION'
      ? (config.fee?.amount ?? 0) * selectedDays.length
      : (config.fee?.amount ?? 0);

  return {
    description: config.name,
    externalLunch: false,
    ...(config.fee
      ? {
          fee: {
            amount: totalAmount,
            // Altdaten ohne currency/paymentFrequency: Feld weglassen statt
            // undefined schreiben (Firestore Admin SDK wirft sonst).
            ...(config.fee.currency ? { currency: config.fee.currency } : {}),
            ...(config.fee.paymentFrequency
              ? { paymentFrequency: config.fee.paymentFrequency }
              : {}),
          } as NonNullable<Lunch['fee']>,
        }
      : {}),
  };
}
